import NetInfo from '@react-native-community/netinfo'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import theme from '../../../../theme'
import { useNavigateTabsStore } from '../../../redux/zustand/navigateTabsStore'
import { useTabStore } from '../../../redux/zustand/tabsStore'
import { getAlert } from './AlertsContext'
import { UserServices } from '../../../../database/user.database'
import { useFooterApp } from '../../../redux/zustand/footerAppStore'
import { Platform } from 'react-native'
import { useBottomSheet } from '../../customUtils/context/BottomSheetContext'
// RightSidebar solo existe en web; proteger el import/uso
let useRightSidebarSafe = null
try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    useRightSidebarSafe =
        require('../../web/context/RightSidebarContext').useRightSidebar
} catch (e) {
    // no-op fuera de web
}

const ThemeContext = createContext()

export const COLOR_THEME_HEADER = [
    {
        route: 'BenefitsHome',
        color: theme.colors.colorPrimary,
    },
    {
        route: 'Notifications',
        color: 'white',
    },
    {
        route: 'PostsList',
        color: 'white',
    },
    {
        route: 'Portfolio',
        color: 'white',
    },
]

const DEFAULT_COLOR = 'white'

export const find_color = (route) => {
    const match = COLOR_THEME_HEADER.find((obj) => obj.route === route)

    if (match) {
        return match.color
    } else {
        return DEFAULT_COLOR
    }
}

export const ThemeProvider = ({ children }) => {
    const [backgroundColor, setBackgroundColor] = useState(DEFAULT_COLOR) // Color de fondo inicial

    const setAppBackgroundColor = (color) => {
        setBackgroundColor(color)
    }

    const setResetBackgroundColor = () => {
        setBackgroundColor(DEFAULT_COLOR)
    }

    const value = {
        backgroundColor,
        setAppBackgroundColor,
        setResetBackgroundColor,
    }

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
    return useContext(ThemeContext)
}

const ConnectionStatePro = createContext()

export const THEME_COLOR_ALERT = {
    SUCCESS: 'rgba(0, 158, 77, 0.8)',
    ERROR: 'rgba(229, 29, 13, 0.8)',
}

export const ConnectionState = ({ children }) => {
    const [isConnected, setConnected] = useState(true)
    const [hasInternet, setHasInternet] = useState(true)
    const { setAlert } = getAlert()
    const [wasPreviouslyConnected, setWasPreviouslyConnected] = useState(null)
    // Estado derivado estable con periodo de gracia para evitar falsos positivos
    const [stableConnected, setStableConnected] = useState(true)
    const offlineTimerRef = React.useRef(null)
    const onlineTimerRef = React.useRef(null)

    useEffect(() => {
        // Mostrar alertas solo cuando el estado estable cambia
        if (wasPreviouslyConnected === null) {
            setWasPreviouslyConnected(stableConnected)
            return
        }
        if (wasPreviouslyConnected === false && stableConnected === true) {
            setAlert(
                '¡Conexión restablecida!',
                'white',
                'wifi',
                THEME_COLOR_ALERT.SUCCESS,
            )
        } else if (wasPreviouslyConnected === true && stableConnected === false) {
            setAlert(
                'No tienes conexión',
                'white',
                'wifi-off',
                THEME_COLOR_ALERT.ERROR,
            )
        }
        setWasPreviouslyConnected(stableConnected)
    }, [stableConnected])

    useEffect(() => {
        const unsubscribe = NetInfo.addEventListener((state) => {
            // Tratar isInternetReachable null/undefined como "desconocido" (no asumimos falso)
            const nextIsConnected = state.isConnected === true
            const reach = state.isInternetReachable
            const nextHasInternet = reach === false ? false : true

            // Actualizar flags crudos (para quienes los lean), pero con mapping seguro
            setIsConnected(nextIsConnected)
            setHasInternet(nextHasInternet)

            // Determinar candidato a conectado/desconectado
            const candidateConnected = nextIsConnected && nextHasInternet

            // Aplicar periodo de gracia con timers
            if (candidateConnected) {
                // Cancelar cualquier intento de marcar offline
                if (offlineTimerRef.current) {
                    clearTimeout(offlineTimerRef.current)
                    offlineTimerRef.current = null
                }
                // Evitar flicker al reconectar: esperar un breve tiempo estable
                if (!stableConnected) {
                    if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
                    onlineTimerRef.current = setTimeout(() => {
                        setStableConnected(true)
                        onlineTimerRef.current = null
                    }, 300)
                } else {
                    // Ya estamos online de forma estable
                    if (onlineTimerRef.current) {
                        clearTimeout(onlineTimerRef.current)
                        onlineTimerRef.current = null
                    }
                }
            } else {
                // Posible desconexión: aplicar gracia para evitar falsos negativos temporales
                if (onlineTimerRef.current) {
                    clearTimeout(onlineTimerRef.current)
                    onlineTimerRef.current = null
                }
                if (!offlineTimerRef.current) {
                    offlineTimerRef.current = setTimeout(() => {
                        setStableConnected(false)
                        offlineTimerRef.current = null
                    }, 1200) // 1.2s de gracia antes de mostrar "sin conexión"
                }
            }
        })

        return () => {
            unsubscribe()
            if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current)
            if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
        }
    }, [])

    const setIsConnected = (value) => {
        setConnected(value)
    }

    const value = {
        setIsConnected,
        isConnected,
        hasInternet,
        // Exponer estado efectivo estable por si algún consumidor lo requiere
        connectedStable: stableConnected,
    }

    return (
        <ConnectionStatePro.Provider value={value}>
            {children}
        </ConnectionStatePro.Provider>
    )
}

export const getConection = () => {
    return useContext(ConnectionStatePro)
}

const ButtonDownloadContext = createContext()

export const ButtonDownloadContextProvider = ({ children }) => {
    const [linkDownload, setLinkDownload] = useState('')
    const [isLinkDownload, setIsLinkDownload] = useState(false)

    const value = {
        linkDownload,
        setLinkDownload,
        setIsLinkDownload,
        isLinkDownload,
    }

    return (
        <ButtonDownloadContext.Provider value={value}>
            {children}
        </ButtonDownloadContext.Provider>
    )
}

export const useButtonDownloadContext = () => {
    const context = useContext(ButtonDownloadContext)
    if (!context) {
        throw new Error(
            'useButtonDownloadContext must be used within a ButtonDownloadContextProvider',
        )
    }
    return context
}
//Este Provider se podría usar para valores x sin necesidad de estar creando siempre alguno
const DataStatePro = createContext()

export const DataState = ({ children, route }) => {
    const [footerRoute, setFooterRoute] = useState('PostsList')
    const [prevFooterRoute, setPrevFooterRoute] = useState('')
    const [routeStack, setRouteStack] = useState([{ route: 'PostsList', params: {} }])
    const [showFooter, setShowFooter] = useState(true)
    const [customBack, setCustomBack] = useState(false)
    const [navigationParams, setNavigationParams] = useState({})
    const tagsDis = useTabStore((state) => state.tagsDis)
    const setIndexTab = useNavigateTabsStore((state) => state.setIndexTab)
    const [isVisibleTabBar, setIsVisibleTabBar] = useState(true)
    const [storesInformation, setStoresInformation] = useState(null)

    const footer_app = useFooterApp((state) => state.footer_app)
    // Flags globales de overlays/widgets
    const { isOpen: isBottomSheetOpen } = useBottomSheet()
    let isRightSidebarOpen = false
    if (Platform.OS === 'web' && typeof useRightSidebarSafe === 'function') {
        try {
            const { activeRightSidebar } = useRightSidebarSafe()
            isRightSidebarOpen = !!activeRightSidebar
        } catch (e) {
            isRightSidebarOpen = false
        }
    }
    // Aplica un stack de navegación ({route, params}[]) al estado visible de
    // la app, sin tocar window.history. Es el único lugar que sabe "cómo se
    // ve" un stack dado — lo usan tanto goBackInternal (navegación que arranca
    // la propia app) como el listener de popstate (navegación que ya disparó
    // el navegador, para atrás O para adelante).
    const applyStack = (newStack) => {
        const stack =
            newStack && newStack.length ? newStack : [{ route: 'PostsList', params: {} }]
        const target = stack[stack.length - 1]

        const index =
            footer_app && footer_app.length > 0
                ? footer_app.findIndex((item) => item.label === target.route)
                : -1
        setIsVisibleTabBar(index !== -1)
        setPrevFooterRoute(footerRoute)
        setFooterRoute(target.route)
        setNavigationParams(target.params || {})
        setRouteStack(stack)
    }

    useEffect(() => {
        if (!route) return
        setCurrentFooterRoute(route || '')
    }, [route])
    const setCurrentFooterRoute = (value, params = {}) => {
        // console.log('[DataState] setCurrentFooterRoute:', value, 'params:', params)
        setNavigationParams(params)
        if (validateCurrentFooterRoute(value)) {
            const tabs = tagsDis
            setIndexTab(tabs && tabs.length > 0 ? tabs[0].index : 1)
        }

        const index =
            footer_app && footer_app.length > 0
                ? footer_app.findIndex((item) => item.label === value)
                : -1

        if (index !== -1) {
            // la view pertenece al footer
            setIsVisibleTabBar(true)
        } else {
            setIsVisibleTabBar(false)
        }
        if (footerRoute !== value) {
            // Actualizar prev y apilar nueva ruta (junto con sus params), evitando duplicados consecutivos
            setPrevFooterRoute(footerRoute)
            setRouteStack((prev) => {
                const last = prev[prev.length - 1]
                if (last && last.route === value) return prev
                const newStack = [...prev, { route: value, params }]

                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    // Guardamos el stack COMPLETO en el history.state (no solo
                    // el nombre de ruta): así, cuando el usuario navegue con
                    // atrás/adelante del navegador, el listener de popstate
                    // puede reconstruir exactamente qué había en cada entrada
                    // en vez de tener que adivinar la dirección.
                    try {
                        window.history.pushState(
                            { __dotsApp: true, stack: newStack },
                            '',
                        )
                    } catch (e) {
                        // no-op
                    }
                }

                return newStack
            })
            setFooterRoute(value)
        }
    }

    // Retrocede un nivel en el stack de navegación. Esta es una navegación
    // que arranca la propia app (p.ej. cerrar el modal de Survey desde
    // SurveyModal), no una reacción a que el usuario ya movió el historial
    // del navegador — por eso acá SÍ corresponde empujar el nuevo stack a
    // window.history. El listener de popstate de más abajo NO pasa por esta
    // función: aplica el stack directamente con applyStack para no volver a
    // pisarle al navegador la entrada que él mismo acaba de mover (eso era lo
    // que hacía que el botón atrás pareciera no reaccionar al primer click).
    const goBackInternal = () => {
        setRouteStack((prev) => {
            if (!prev || prev.length <= 1) {
                // No hay historial, permanecer en la actual
                return prev && prev.length ? prev : [{ route: 'PostsList', params: {} }]
            }
            const newStack = prev.slice(0, -1)
            const target = newStack[newStack.length - 1] || {
                route: 'PostsList',
                params: {},
            }

            const index =
                footer_app && footer_app.length > 0
                    ? footer_app.findIndex((item) => item.label === target.route)
                    : -1
            setIsVisibleTabBar(index !== -1)
            setPrevFooterRoute(footerRoute)
            setFooterRoute(target.route)
            // Restaurar los params que tenía la pantalla a la que volvemos
            // (en vez de vaciarlos, lo que rompía pantallas que dependen
            // de params para renderizar, ej. un curso sin su courseItem)
            setNavigationParams(target.params || {})

            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                try {
                    window.history.pushState({ __dotsApp: true, stack: newStack }, '')
                } catch (e) {
                    // no-op
                }
            }

            return newStack
        })
    }

    // Sincronizar los botones atrás/adelante del navegador (web) con el stack
    // interno de navegación. Se guarda en un ref para que el listener del
    // evento 'popstate' (que corre fuera del ciclo de render de React)
    // siempre lea el stack/flags más recientes.
    const webBackRef = useRef({ routeStack, isBottomSheetOpen, isRightSidebarOpen })
    useEffect(() => {
        webBackRef.current = { routeStack, isBottomSheetOpen, isRightSidebarOpen }
    })

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') return

        if (!window.history.state || !window.history.state.__dotsApp) {
            try {
                window.history.replaceState(
                    {
                        __dotsApp: true,
                        stack: [{ route: 'PostsList', params: {} }],
                    },
                    '',
                )
            } catch (e) {
                // no-op
            }
        }

        const handlePopState = (event) => {
            const {
                isBottomSheetOpen: sheetOpen,
                isRightSidebarOpen: sidebarOpen,
                routeStack: currentStack,
            } = webBackRef.current
            if (sheetOpen || sidebarOpen) {
                // Hay un overlay abierto (bottom sheet / sidebar): no navegamos,
                // solo reponemos el estado para que el usuario no salga de la app.
                try {
                    window.history.pushState(
                        { __dotsApp: true, stack: currentStack },
                        '',
                    )
                } catch (e) {
                    // no-op
                }
                return
            }

            const state = event.state
            if (state && state.__dotsApp && Array.isArray(state.stack) && state.stack.length) {
                // El navegador nos dice exactamente qué pantalla (y con qué
                // params) corresponde a la entrada a la que se movió — sirve
                // igual para atrás que para adelante, porque no asumimos
                // dirección: solo reflejamos lo que diga el historial.
                applyStack(state.stack)
            } else {
                // Entrada sin nuestro formato (INIT u otra previa): volvemos a la raíz.
                applyStack([{ route: 'PostsList', params: {} }])
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])

    const validateCurrentFooterRoute = (value) => {
        const map = {
            ScheduleDistributor: false,
            AddStores: false,
            StoreDetail: false,
            ExecutionView: false,
        }

        return map.hasOwnProperty(value) ? map[value] : true
    }
    const calledCustomBack = () => {
        setCustomBack(!customBack)
    }

    const value = {
        footerRoute,
        setCurrentFooterRoute,
        isVisibleTabBar,
        prevFooterRoute,
        routeStack,
        goBackInternal,
        customBack,
        calledCustomBack,
        showFooter,
        navigationParams,
        setNavigationParams,
        // Exponer flags para navegación web/back
        isBottomSheetOpen: !!isBottomSheetOpen,
        isRightSidebarOpen: !!isRightSidebarOpen,
        storesInformation,
        setStoresInformation,
    }
    return <DataStatePro.Provider value={value}>{children}</DataStatePro.Provider>
}

export const getData = () => {
    return useContext(DataStatePro)
}
